import {
  Check,
  ChevronDown,
  History,
  Search,
  Tags
} from "lucide-react";
import { Fragment, useCallback, useEffect, useState } from "react";
import { apiClient, type ClassificationEdit } from "@/shared/api/api-client";
import { groupOptions, isGrouped } from "@/shared/components/group-options";
import { describeEdit, describeRevert } from "@/features/mentions/edit-diff";
import { formatDateTime } from "@/shared/utils/format";

/**
 * Peças da classificação que sobraram depois que o editor de menção
 * saiu (2026-10-19, decisão da dona: tema, mensagem-chave, porta-voz,
 * polaridade, abordagem, destaque e revisão se editam na própria linha —
 * card e Modo revisão —, então o formulário em pop-up não serve mais):
 *
 * - `CatalogMultiSelect`: a caixa de escolha múltipla do lote e do
 *   vincular a outra marca;
 * - `fieldLabels`: o rótulo de cada campo da trilha, que o reprocessamento
 *   por IA também usa;
 * - `MentionClassificationHistory`: a trilha de edição do drawer e do
 *   modal de detalhes.
 *
 * O nome do arquivo ficou: renomeá-lo mexeria em seis importadores sem
 * mudar nada do que eles fazem.
 */

export const fieldLabels: Record<string, string> = {
  polarity: "Polaridade",
  approach: "Abordagem",
  theme_ids: "Subtemas",
  theme_percentages: "Peso dos subtemas",
  highlight_theme_id: "Subtema em destaque",
  reviewed: "Revisão",
  linked: "Vínculo manual",
  discarded: "Descarte",
  key_message_ids: "Mensagens-chave",
  spokesperson_ids: "Porta-vozes"
};

/** De onde veio a linha da trilha. Manual não ganha rótulo: é o caso de
 *  sempre, e linha anterior a 2026-10-05 (`kind` nulo) também é manual. */
const kindLabels: Record<string, string> = {
  bulk_edit: "Correção em lote",
  ai_reprocess: "Reprocessamento por IA",
  revert: "Volta de versão"
};

/**
 * Seleção múltipla que abre e fecha, no mesmo molde do filtro do
 * Dashboard (`DashboardMultiFilter`) — reusa as classes dele.
 *
 * É `<details>` e não uma lista aberta porque estes catálogos crescem:
 * uma empresa com trinta temas transformaria o formulário numa parede
 * de botões, e o que importa ao abrir a menção é ver O QUE ESTÁ
 * selecionado, não o catálogo inteiro. Fechado, o resumo mostra
 * exatamente isso.
 *
 * Sem o par "Incluir/Excluir" do Dashboard: lá é filtro (pode-se querer
 * o complemento), aqui é atribuição — não existe "menção que não tem
 * este tema" como valor a gravar.
 *
 * Exportada desde 2026-09-21 porque o editor EM LOTE
 * (`BulkClassificationDialog`) usa a mesma caixa — uma forma só de
 * escolher tema, mensagem e porta-voz na plataforma.
 */
export function CatalogMultiSelect({
  label,
  options,
  selected,
  onToggle,
  onClear
}: {
  label: string;
  /** `group` = título sob o qual a opção aparece (tema -> grupo de
   *  tema). Cada opção tem UM grupo, e nenhuma com grupo = lista plana. */
  options: Array<{ id: number; name: string; group?: string | null }>;
  selected: number[];
  onToggle: (id: number) => void;
  onClear: () => void;
}) {
  const [search, setSearch] = useState("");
  if (!options.length) return null;

  const grouped = isGrouped(options, option => option.group);
  const blocks = groupOptions(options, search, {
    labelOf: option => option.name,
    groupOf: option => option.group
  });
  const chosen = options.filter(option => selected.includes(option.id));
  // "Marcar todas": só o que está à vista (a busca recorta) e ainda não
  // está marcado — o contrato dos chamadores é `onToggle(id)`, então
  // chamar nos já marcados os desmarcaria.
  const unmarkedVisible = [...new Set(blocks.flatMap(block => block.options.map(option => option.id)))]
    .filter(id => !selected.includes(id));

  return (
    <details className="multi-filter classification-editor-select">
      <summary>
        <Tags size={15} aria-hidden="true"/>
        <span>{label}</span>
        {/* Resumo do que está selecionado — é a informação que a pessoa
            procura ao abrir a menção. Acima de 2, vira contagem pra não
            estourar a linha. */}
        <small>
          {chosen.length === 0
            ? "Nenhum"
            : chosen.length <= 2
              ? chosen.map(option => option.name).join(", ")
              : `${chosen.length} selecionados`}
        </small>
        <ChevronDown size={16} aria-hidden="true"/>
      </summary>
      <div className="multi-filter-body">
        <div className="classification-editor-select-head">
          {/* UM botão que alterna (dois lado a lado estouravam a largura). */}
          {unmarkedVisible.length ? (
            <button
              type="button"
              className="text-button"
              onClick={() => unmarkedVisible.forEach(id => onToggle(id))}
            >
              Marcar todas
            </button>
          ) : (
            <button
              type="button"
              className="text-button"
              onClick={onClear}
              disabled={!selected.length}
            >
              Limpar seleção
            </button>
          )}
        </div>
        {options.length > 8 && (
          <div className="multi-filter-search">
            <Search size={15} aria-hidden="true"/>
            <input
              type="search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={`Buscar em ${label}…`}
              aria-label={`Buscar opções de ${label}`}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        )}
        <div className="multi-filter-options filter-chip-group">
          {blocks.map(block => (
            <Fragment key={block.group ?? "\u0000"}>
              {grouped && (
                <h5 className="multi-filter-group-title">
                  {block.group ?? "Sem tema"}
                </h5>
              )}
              {block.options.map(option => (
                <button
                  key={option.id}
                  type="button"
                  className={selected.includes(option.id) ? "active" : ""}
                  aria-pressed={selected.includes(option.id)}
                  onClick={() => onToggle(option.id)}
                >
                  {selected.includes(option.id) && (
                    <Check size={13} aria-hidden="true"/>
                  )}
                  {option.name}
                </button>
              ))}
            </Fragment>
          ))}
          {!blocks.length && (
            <p className="multi-filter-empty">Nenhuma opção encontrada.</p>
          )}
        </div>
      </div>
    </details>
  );
}

/** A trilha da classificação: correção manual, em lote e reprocessamento
 * por IA, cada linha com quem fez (ou pediu) e quando. Só aparece quando
 * existe alguma — uma menção nunca editada não precisa de uma seção
 * vazia. */
export function MentionClassificationHistory({
  mentionId,
  token,
  reloadKey
}: {
  mentionId: number;
  token: string;
  reloadKey: number;
}) {
  const [edits, setEdits] = useState<ClassificationEdit[]>([]);
  // Recolhido por padrão (2026-10-13): o título diz quantas edições há,
  // e quem quer ver abre. A busca acontece igual, pra o número existir.
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setEdits(await apiClient.mentionClassificationHistory(token, mentionId));
    } catch {
      /* o histórico é secundário — falhar aqui não pode derrubar o
         drawer inteiro */
    }
  }, [token, mentionId]);

  useEffect(() => {
    void load();
  }, [load, reloadKey]);

  if (!edits.length) return null;

  return (
    <div className={`classification-history${open ? " is-open" : ""}`}>
      <button
        type="button"
        className="classification-history-head classification-history-toggle"
        aria-expanded={open}
        onClick={() => setOpen(current => !current)}
      >
        <History size={14} aria-hidden="true"/>
        Histórico de classificação
        <span className="classification-history-count">{edits.length}</span>
        <ChevronDown size={14} className="classification-history-caret" aria-hidden="true"/>
      </button>
      {open && (
      <ul>
        {edits.map(edit => {
          const revert = describeRevert(edit, iso => formatDateTime(new Date(iso)));
          return (
          <li key={edit.id}>
            <span>
              {formatDateTime(new Date(edit.changed_at))} ·{" "}
              {/* Foto do nome na hora da edição; sem ela (edição antiga
                  ou stub de header), o `sub`. */}
              <span title={edit.changed_by}>
                {edit.changed_by_name ?? edit.changed_by}
              </span>
              {/* No reprocessamento, quem aparece é quem PEDIU — a IA
                  fez a mudança em nome dela. */}
              {edit.kind && kindLabels[edit.kind] && (
                <>
                  {" "}· <strong>{kindLabels[edit.kind]}</strong>
                </>
              )}
            </span>
            {/* O que mudou, campo a campo (2026-10-07): "Temas: + A · − B",
                "Polaridade: Negativo → Positivo". Os nomes vêm do
                servidor em `labels`; ver `edit-diff.ts`. */}
            <ul className="classification-history-changes">
              {revert && <li>{revert}</li>}
              {describeEdit(edit).map(line => (
                <li key={line.field}>
                  <em>{line.label}:</em> {line.text}
                </li>
              ))}
            </ul>
          </li>
          );
        })}
      </ul>
      )}
    </div>
  );
}
